import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../App";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, ClipboardPaste, CheckCircle2 } from "lucide-react";
import { KPI_FIELDS, kpiToStore } from "../lib/kpiFields";

/**
 * Production backfill — grid + paste-from-Excel. Bulk-loads historical assets
 * into production_assets (the single source of truth) via POST
 * /production/assets/bulk. Preview is a dry-run (ready/warn/duplicate/error per
 * row); Import commits. (.xlsx file upload is a planned follow-up.)
 */

const COLS = [
  // core
  { k: "title", label: "Title", w: 200, hint: "required" },
  { k: "account", label: "Account / Brand", w: 150, hint: "required" },
  { k: "asset_type", label: "Type", w: 120, hint: "reel / ad_film / static / carousel / story / performance / thumbnail" },
  { k: "stage", label: "Stage", w: 130, hint: "Concept / Script / Edit / Live …" },
  { k: "platform", label: "Platform", w: 120, hint: "instagram / youtube / meta_ads …" },
  // go-live
  { k: "go_live", label: "Planned go-live *", w: 130, hint: "YYYY-MM-DD · required" },
  { k: "revised_go_live", label: "Revised go-live", w: 130, hint: "YYYY-MM-DD · optional" },
  { k: "actual_go_live", label: "Actual go-live", w: 130, hint: "YYYY-MM-DD · optional" },
  // team (resolved by name)
  { k: "producer", label: "Producer", w: 120 },
  { k: "creative_lead", label: "Creative Lead", w: 120 },
  { k: "writer", label: "Writer", w: 110 },
  { k: "director", label: "Director", w: 110 },
  { k: "dop", label: "DOP", w: 100 },
  { k: "editor", label: "Editor", w: 110 },
  { k: "designer", label: "Designer", w: 110 },
  { k: "line_producer", label: "Line Producer", w: 120 },
  // links
  { k: "brief_link", label: "Brief Link", w: 150 },
  { k: "script_link", label: "Script Link", w: 150 },
  { k: "frame_io_link", label: "Frame.io Link", w: 150 },
  { k: "live_url", label: "Live URL", w: 160 },
  // performance KPIs — full organic + paid set (see lib/kpiFields)
  ...KPI_FIELDS.map((f) => ({ k: f.key, label: f.label, w: f.unit === "num" ? 110 : 90, kpi: true })),
  { k: "description", label: "Notes", w: 220 },
];
// Categorical columns become dropdowns (free-text typos were the #1 cause of
// rows failing to import). [value, label] pairs; account options are loaded live.
const TYPE_OPTS = [
  ["reel", "Reel"], ["ad_film", "Ad Film"], ["static", "Static"], ["carousel", "Carousel"],
  ["story", "Story"], ["thumbnail", "Thumbnail"], ["performance", "Performance"],
];
// Exhaustive across both production pipelines (backend FULL_PIPELINE +
// DESIGN_PIPELINE): video stages plus the design-only Design Brief / Design V1.
const STAGE_OPTS = [
  "Concept", "Script", "Treatment", "Design Brief", "Pre-Production", "Shoot Scheduled",
  "Shooting", "Edit", "Design V1", "Internal Review", "Client Review", "Revisions",
  "Approved", "Live",
].map((s) => [s, s]);
const PLATFORM_OPTS = [
  ["instagram", "Instagram"], ["youtube", "YouTube"], ["meta_ads", "Meta Ads"],
  ["google_ads", "Google Ads"], ["mainline", "Mainline / TV"], ["digital", "Digital"],
];
const SELECT_COLS = { asset_type: TYPE_OPTS, stage: STAGE_OPTS, platform: PLATFORM_OPTS };
// Team columns become searchable people-pickers (loaded live from /users — the
// backfill resolves team members by name). Date columns become native date
// pickers. Both still accept typed/pasted custom values so Paste-from-Excel is safe.
const TEAM_KEYS = new Set(["producer", "creative_lead", "writer", "director", "dop", "editor", "designer", "line_producer"]);
const DATE_KEYS = new Set(["go_live", "revised_go_live", "actual_go_live"]);

const BLANK = () => COLS.reduce((o, c) => ({ ...o, [c.k]: "" }), {});
const STATUS_BG = { ready: "transparent", warn: "#fff7ed", duplicate: "#f3f4f6", error: "#fef2f2" };
const STATUS_FG = { ready: "#15803d", warn: "#b45309", duplicate: "#6b7280", error: "#b91c1c" };

export default function ProductionBackfill() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(() => Array.from({ length: 10 }, BLANK));
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [accounts, setAccounts] = useState([]);

  // Account dropdown — same source as the Production "new asset" form (/leads,
  // all stages). Backfill resolves the account by name, so we offer the names.
  useEffect(() => {
    api.get("/leads").then((r) => setAccounts(r.data || [])).catch(() => {});
  }, []);
  const accountOpts = useMemo(() => {
    const names = Array.from(new Set((accounts || []).map((a) => a.company_name).filter(Boolean)));
    names.sort((a, b) => a.localeCompare(b));
    return names.map((n) => [n, n]);
  }, [accounts]);

  // Team-member dropdown — people from /users. Backfill resolves team by name,
  // so we offer the display names (custom typed names still allowed).
  const [users, setUsers] = useState([]);
  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {});
  }, []);
  const userOpts = useMemo(() => {
    const names = Array.from(new Set((users || []).map((u) => u.full_name || u.name || u.email).filter(Boolean)));
    names.sort((a, b) => a.localeCompare(b));
    return names.map((n) => [n, n]);
  }, [users]);

  const setCell = (i, k, v) => { setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [k]: v } : row))); setPreview(null); };
  const addRow = () => setRows((r) => [...r, BLANK()]);
  const delRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));

  // Excel copies as TSV. Map cells to columns in order.
  const applyPaste = () => {
    const parsed = pasteText.trim().split(/\r?\n/).filter(Boolean).map((line) => {
      const cells = line.split("\t");
      const row = BLANK();
      COLS.forEach((c, idx) => { if (cells[idx] != null) row[c.k] = String(cells[idx]).trim(); });
      return row;
    });
    if (!parsed.length) { toast.error("Nothing to paste"); return; }
    setRows((r) => [...r.filter((x) => COLS.some((c) => x[c.k])), ...parsed]);
    setPasteText(""); setPasteOpen(false); setPreview(null);
    toast.success(`${parsed.length} rows added`);
  };

  const payloadRows = useCallback(() => rows
    .filter((r) => r.title || r.account)
    .map((r) => {
      const kpis = {};
      KPI_FIELDS.forEach((f) => { const v = kpiToStore(f, r[f.key]); if (v !== undefined) kpis[f.key] = v; });
      return {
        title: r.title, account: r.account, asset_type: r.asset_type, stage: r.stage,
        platform: r.platform, description: r.description,
        go_live: r.go_live, revised_go_live: r.revised_go_live, actual_go_live: r.actual_go_live,
        producer: r.producer, creative_lead: r.creative_lead, writer: r.writer,
        director: r.director, dop: r.dop, editor: r.editor, designer: r.designer,
        line_producer: r.line_producer,
        brief_link: r.brief_link, script_link: r.script_link, frame_io_link: r.frame_io_link,
        live_url: r.live_url,
        kpis,
      };
    }), [rows]);

  const run = async (dry) => {
    const rs = payloadRows();
    if (!rs.length) { toast.error("Add at least one row — fill Title + Account at minimum"); return; }
    setBusy(true);
    try {
      const res = await api.post("/production/assets/bulk", { rows: rs, dry_run: dry });
      setPreview(res.data); // always show per-row status (preview AND import)
      if (dry) return;
      const created = res.data.created || 0;
      if (created > 0) {
        toast.success(`Imported ${created} asset${created > 1 ? "s" : ""} into Production`);
        navigate("/production");
      } else {
        const c = res.data.counts || {};
        const why = [c.duplicate ? `${c.duplicate} duplicate` : "", c.error ? `${c.error} with errors` : ""]
          .filter(Boolean).join(", ");
        toast.error(`Nothing imported${why ? ` — ${why}. See the Check column.` : " — check the Check column."}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed — try Preview to see per-row issues");
    } finally { setBusy(false); }
  };

  const importable = preview ? preview.counts.ready + preview.counts.warn : 0;

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg, #fafaf9)", paddingBottom: 80 }}>
      <div style={{ maxWidth: 1180, margin: "0 auto", padding: "24px 20px 0" }}>
        <button onClick={() => navigate("/production")} className="text-[#6b7280]" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, background: "none", border: "none", cursor: "pointer", marginBottom: 14 }}>
          <ArrowLeft size={15} /> Production
        </button>

        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 4 }}>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0, color: "var(--t1, #111)" }}>Add assets</h1>
            <div style={{ fontSize: 13, color: "#9ca3af", marginTop: 2 }}>New or historical — type or paste from Excel. These land in Production; Reviews reads from here.</div>
          </div>
          <button onClick={() => setPasteOpen((v) => !v)} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, padding: "8px 14px", border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff", cursor: "pointer" }}>
            <ClipboardPaste size={15} /> Paste from Excel
          </button>
        </div>

        {pasteOpen && (
          <div style={{ margin: "12px 0", padding: 14, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12 }}>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>
              Copy rows from the tracker and paste below. Column order: {COLS.map((c) => c.label).join(" · ")}
            </div>
            <textarea value={pasteText} onChange={(e) => setPasteText(e.target.value)} rows={5}
              placeholder="Paste tab-separated rows here…"
              style={{ width: "100%", fontSize: 12, fontFamily: "monospace", border: "1px solid #e5e7eb", borderRadius: 8, padding: 10, resize: "vertical" }} />
            <div style={{ marginTop: 8, textAlign: "right" }}>
              <button onClick={applyPaste} style={{ fontSize: 13, padding: "7px 14px", background: "var(--t1, #111)", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer" }}>Add rows</button>
            </div>
          </div>
        )}

        {preview && (
          <div style={{ margin: "14px 0", padding: "12px 16px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, fontSize: 13, display: "flex", gap: 16, flexWrap: "wrap" }}>
            <span style={{ color: "#111", fontWeight: 600 }}>{preview.counts.total} rows</span>
            <span style={{ color: "#15803d" }}>{preview.counts.ready} ready</span>
            {preview.counts.warn > 0 && <span style={{ color: "#b45309" }}>{preview.counts.warn} warn</span>}
            {preview.counts.duplicate > 0 && <span style={{ color: "#6b7280" }}>{preview.counts.duplicate} duplicate (skipped)</span>}
            {preview.counts.error > 0 && <span style={{ color: "#b91c1c" }}>{preview.counts.error} error</span>}
          </div>
        )}

        <div style={{ overflowX: "auto", border: "1px solid #eee", borderRadius: 12, background: "#fff", marginTop: 14 }}>
          <table style={{ borderCollapse: "collapse", fontSize: 12.5, minWidth: 1040 }}>
            <thead>
              <tr style={{ textAlign: "left", color: "#6b7280", background: "#fafafa" }}>
                <th style={{ width: 36, padding: "9px 8px" }}></th>
                {COLS.map((c) => <th key={c.k} style={{ fontWeight: 600, padding: "9px 8px", width: c.w }}>{c.label}</th>)}
                <th style={{ padding: "9px 8px", width: 150 }}>Check</th>
                <th style={{ width: 36 }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const blank = !COLS.some((c) => row[c.k]);
                return (
                  <tr key={i} style={{ borderTop: "1px solid #f0f0f0" }}>
                    <td style={{ padding: "4px 8px", color: "#cbd5e1", textAlign: "center" }}>{i + 1}</td>
                    {COLS.map((c) => {
                      const opts = c.k === "account" ? accountOpts
                        : TEAM_KEYS.has(c.k) ? userOpts
                        : SELECT_COLS[c.k];
                      const isDate = DATE_KEYS.has(c.k);
                      return (
                        <td key={c.k} style={{ padding: "4px 6px" }}>
                          {isDate ? (
                            <input type="date" value={row[c.k] || ""} onChange={(e) => setCell(i, c.k, e.target.value)}
                              style={{ width: c.w, height: 30, fontSize: 12, border: "1px solid #e5e7eb", borderRadius: 6, padding: "0 8px", background: "#fff", color: row[c.k] ? "#111" : "#9ca3af", boxSizing: "border-box" }} />
                          ) : opts ? (
                            <Combobox value={row[c.k]} opts={opts} width={c.w}
                              placeholder={c.k === "account" ? "Search account…" : TEAM_KEYS.has(c.k) ? "Search person…" : "Search…"}
                              onChange={(v) => setCell(i, c.k, v)} />
                          ) : (
                            <input value={row[c.k]} onChange={(e) => setCell(i, c.k, e.target.value)} placeholder={c.hint || ""}
                              style={{ width: c.w, height: 30, fontSize: 12, border: "1px solid #e5e7eb", borderRadius: 6, padding: "0 8px", background: "#fff" }} />
                          )}
                        </td>
                      );
                    })}
                    <td style={{ padding: "4px 8px" }}>
                      <RowStatus s={preview ? matchStatus(preview, rows, i) : null} blank={blank} />
                    </td>
                    <td style={{ padding: "4px 8px", textAlign: "center" }}>
                      {rows.length > 1 && <button onClick={() => delRow(i)} style={{ background: "none", border: "none", cursor: "pointer", color: "#cbd5e1" }}><Trash2 size={14} /></button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginTop: 14 }}>
          <button onClick={addRow} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, padding: "8px 14px", border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff", cursor: "pointer" }}>
            <Plus size={15} /> Add row
          </button>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => run(true)} disabled={busy} style={{ fontSize: 13, padding: "9px 16px", border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff", cursor: busy ? "wait" : "pointer" }}>
              {busy ? "Checking…" : "Preview"}
            </button>
            <button onClick={() => run(false)} disabled={busy}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, padding: "9px 18px", background: busy ? "#e5e7eb" : "var(--r, #dc2626)", color: busy ? "#9ca3af" : "#fff", border: "none", borderRadius: 8, cursor: busy ? "wait" : "pointer" }}>
              <CheckCircle2 size={15} /> {busy ? "Importing…" : `Import${preview && importable ? ` ${importable}` : ""}`}
            </button>
          </div>
        </div>
        <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 10 }}>Just hit <b style={{ color: "#6b7280" }}>Import</b> — it validates and adds in one step (Preview is optional for a dry check). Need only Title, Account, Type &amp; Planned go-live; an unknown stage falls back to the first pipeline stage, and new accounts are created automatically. Duplicates (same account + title + go-live month) are skipped.</p>
      </div>
    </div>
  );
}

// The preview returns rows for non-blank inputs in order; align by counting
// non-blank rows up to index i.
function matchStatus(preview, rows, i) {
  if (rows[i] && !COLS.some((c) => rows[i][c.k])) return null;
  let idx = -1;
  for (let j = 0; j <= i; j++) {
    const r = rows[j];
    if (r && (r.title || r.account)) idx++;
  }
  return preview.rows[idx] || null;
}

// Searchable combobox: type to filter the list, click to pick, or just type a
// custom value (preserved — so Paste-from-Excel never drops data). Stores the
// option value (e.g. "ad_film"); shows its label (e.g. "Ad Film") when closed.
function Combobox({ value, opts, width, placeholder, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const labelFor = (v) => { const m = opts.find(([ov]) => ov === v); return m ? m[1] : (v || ""); };
  const f = q.trim().toLowerCase();
  const shown = !f ? opts : opts.filter(([v, label]) => label.toLowerCase().includes(f) || v.toLowerCase().includes(f));
  const inputStyle = { width, height: 30, fontSize: 12, border: "1px solid #e5e7eb", borderRadius: 6,
    padding: "0 8px", background: "#fff", color: value ? "#111" : "#9ca3af", boxSizing: "border-box" };
  return (
    <div ref={ref} style={{ position: "relative", width }}>
      <input
        value={open ? q : labelFor(value)}
        placeholder={placeholder}
        onFocus={() => { setQ(""); setOpen(true); }}
        onChange={(e) => { setQ(e.target.value); onChange(e.target.value); if (!open) setOpen(true); }}
        style={inputStyle}
      />
      {open && (
        <div style={{ position: "absolute", top: 32, left: 0, zIndex: 30, width: Math.max(width, 150),
          maxHeight: 220, overflowY: "auto", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8,
          boxShadow: "0 6px 20px rgba(0,0,0,.12)" }}>
          {shown.length === 0 && (
            <div style={{ padding: "8px 10px", fontSize: 12, color: "#9ca3af" }}>
              {q ? `Use "${q}" (custom)` : "No matches"}
            </div>
          )}
          {shown.map(([v, label]) => (
            <div key={v} onMouseDown={(e) => { e.preventDefault(); onChange(v); setQ(""); setOpen(false); }}
              style={{ padding: "7px 10px", fontSize: 12, cursor: "pointer", color: "#111",
                background: v === value ? "#f3f4f6" : "#fff" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#f7f7f8")}
              onMouseLeave={(e) => (e.currentTarget.style.background = v === value ? "#f3f4f6" : "#fff")}>
              {label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RowStatus({ s, blank }) {
  if (blank || !s) return <span style={{ fontSize: 11, color: "#cbd5e1" }}>—</span>;
  return (
    <span style={{ fontSize: 11, fontWeight: 500, padding: "2px 8px", borderRadius: 5, background: STATUS_BG[s.status], color: STATUS_FG[s.status] }}>
      {s.status}{s.issues && s.issues.length ? ` · ${s.issues[0]}` : ""}
    </span>
  );
}
